import { useState, useEffect } from "react";
import {
  FaArrowLeft,
  FaCircleUser,
  FaPhone,
  FaWifi,
  FaServer,
  FaLocationDot,
  FaTag,
  FaBriefcase,
  FaCalendarCheck,
  FaEthernet,
  FaPlug,
  FaCircleCheck,
  FaClock,
  FaHourglassStart,
  FaRoute,
} from "react-icons/fa6";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";
import { DashboardLayout } from "../../../../../components/layouts/DashboardLayout";
import { Header } from "../../../../../components/Header";

/* ============================================
   Dummy Data PSB Detail
   ============================================ */
const DUMMY_PSB = {
  id: "psb-001",
  id_pel: "PSB-20231027-001",
  name: "Budi Purnomo",
  phone_number: "0812-9876-5432",
  status: "Proses Pemasangan",
  profile: { name: "Paket 30 Mbps" },
  nas: { name: "MikroTik-Core1" },
  pop: { name: "POP Ciledug" },
  odp: { name: "ODP-CLG-05/12" },
  reseller: { name: "Ahmad Fauzi" },
  address: "Jl. Kebon Jeruk No. 17, RT 03/RW 05, Jakarta Barat",
  
  schedules: [
    {
      id: "sch-1",
      date: "2023-10-27",
      time: "09:00",
      technician: "Tim Installasi A",
      status: "Dijadwalkan Ulang",
      reason: "Kabel feeder di ODP penuh, butuh penarikan kabel baru dari POP.",
    },
    {
      id: "sch-2",
      date: "2023-10-29",
      time: "14:00",
      technician: "Tim Installasi B",
      status: "Selesai Dikerjakan",
      reason: null,
    },
    {
      id: "sch-3",
      date: "2023-10-30",
      time: "10:00",
      technician: "Tim Installasi A",
      status: "Akan Datang",
      reason: null,
    },
  ],

  installation_info: {
    cable_type: "Single Mode G.657A1",
    cable_length: "250 Meter",
    modem: "Huawei HG8245H",
    splitter: "1:8 Splitter (Port 5)",
    connector: "SC/APC",
    additional_notes: "Penarikan kabel bawah tanah sepanjang 20m di depan rumah pelanggan. Menggunakan selang pelindung PVC.",
  },
};

/* ============================================
   Status Step Tracker
   ============================================ */
const steps = [
  { key: "Pendaftaran", icon: FaCircleUser },
  { key: "Penjadwalan", icon: FaCalendarCheck },
  { key: "Proses Pemasangan", icon: FaEthernet },
  { key: "Aktif", icon: FaCircleCheck },
];

/* ============================================
   Sub-Komponen: SectionCard
   ============================================ */
const SectionCard = ({ title, icon: Icon, children }) => (
  <article
    className="rounded-2xl p-5 sm:p-6 transition-all duration-300"
    style={{
      background: "rgba(15, 23, 42, 0.5)",
      border: "1px solid rgba(51, 65, 85, 0.3)",
      backdropFilter: "blur(12px)",
    }}
  >
    <div className="flex items-center gap-3 mb-5">
      {Icon && (
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center"
          style={{
            background: "rgba(0, 180, 216, 0.1)",
            border: "1px solid rgba(0, 180, 216, 0.2)",
          }}
        >
          <Icon className="text-ocean-400 text-sm" />
        </div>
      )}
      <h3 className="font-display text-base sm:text-lg font-bold text-gray-100">
        {title}
      </h3>
    </div>
    {children}
  </article>
);

/* ============================================
   Sub-Komponen: StatusBadge (Sama seperti di Index)
   ============================================ */
const StatusBadge = ({ status }) => {
  const isActive = status === "Aktif";
  const isProcess = status === "Proses Pemasangan" || status === "Akan Datang";
  const isReschedule = status === "Dijadwalkan Ulang";

  let styleClass = "bg-slate-500/12 text-slate-400 border-slate-500/20";
  if (isActive || status === "Selesai Dikerjakan") styleClass = "bg-emerald-500/12 text-emerald-400 border-emerald-500/20";
  else if (isProcess) styleClass = "bg-cyan-500/12 text-cyan-400 border-cyan-500/20";
  else if (isReschedule) styleClass = "bg-amber-500/12 text-amber-400 border-amber-500/20";

  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold tracking-wide border ${styleClass}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-emerald-400" : isProcess ? "bg-cyan-400" : isReschedule ? "bg-amber-400" : "bg-slate-400"}`} />
      {status}
    </span>
  );
};

/* ============================================
   Halaman Utama: PsbDetail
   ============================================ */
export const PppoePsbDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [showSidebar, setShowSidebar] = useState(false);
  const [psbData, setPsbData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulasi fetch API berdasarkan ID
    setTimeout(() => {
      setPsbData(DUMMY_PSB);
      setIsLoading(false);
    }, 800);
  }, [id]);

  if (isLoading || !psbData) {
    return (
      <DashboardLayout showSidebar={showSidebar} setShowSidebar={setShowSidebar}>
        <div className="p-6 flex items-center justify-center h-screen">
          <div className="animate-pulse flex flex-col items-center gap-4">
            <div className="w-10 h-10 rounded-full border-4 border-slate-600 border-t-ocean-400 animate-spin" />
            <p className="text-slate-400">Memuat detail PSB...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Logika untuk menentukan step aktif
  const currentStepIndex = steps.findIndex((step) => step.key === psbData.status);

  return (
    <>
      <Helmet>
        <title>Detail PSB {psbData.id_pel} - Radius Trend</title>
      </Helmet>

      <DashboardLayout showSidebar={showSidebar} setShowSidebar={setShowSidebar}>
        {/* ===== Header ===== */}
        <Header toggleSidebar={() => setShowSidebar(!showSidebar)}>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-lg hover:bg-slate-800 transition-colors text-slate-400 hover:text-white"
            >
              <FaArrowLeft />
            </button>
            <div className="flex flex-col">
              <h2 className="text-lg md:text-xl font-display font-bold text-gray-100 tracking-tight">
                Detail PSB
              </h2>
              <p className="text-xs text-slate-500 hidden md:block mt-0.5 font-mono">
                {psbData.id_pel}
              </p>
            </div>
          </div>
          <StatusBadge status={psbData.status} />
        </Header>

        {/* ===== Content ===== */}
        <main className="p-4 lg:p-6 space-y-6">
          
          {/* Row 1: Data PSB & Status Tracker */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* Data Pelanggan PSB */}
            <div className="lg:col-span-2">
              <SectionCard title="Data Pelanggan Baru" icon={FaCircleUser}>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Nama Pelanggan</p>
                      <p className="text-slate-200 font-semibold text-lg">{psbData.name}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Alamat Pemasangan</p>
                      <p className="text-slate-300 text-sm leading-relaxed">{psbData.address}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { icon: FaPhone, label: "Telepon", value: psbData.phone_number, color: "text-ocean-400" },
                      { icon: FaWifi, label: "Profile", value: psbData?.profile?.name, color: "text-violet-400" },
                      { icon: FaServer, label: "NAS", value: psbData?.nas?.name, color: "text-cyan-400" },
                      { icon: FaLocationDot, label: "POP", value: psbData?.pop?.name, color: "text-amber-400" },
                      { icon: FaTag, label: "ODP", value: psbData?.odp?.name, color: "text-emerald-400" },
                      { icon: FaBriefcase, label: "Reseller", value: psbData?.reseller?.name || "Langsung", color: "text-slate-400" },
                    ].map((row, i) => {
                      const Icon = row.icon;
                      return (
                        <div key={i} className="flex items-start gap-2.5 min-w-0">
                          <Icon className={`${row.color} text-sm flex-shrink-0 mt-0.5`} />
                          <div className="min-w-0">
                            <p className="text-[10px] text-slate-600 uppercase font-semibold tracking-wider leading-none">{row.label}</p>
                            <p className="text-sm text-slate-300 truncate mt-1">{row.value || "—"}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </SectionCard>
            </div>

            {/* Status Tracker */}
            <div className="lg:col-span-1">
              <SectionCard title="Status Pemasangan" icon={FaCircleCheck}>
                <div className="space-y-6">
                  {steps.map((step, index) => {
                    const isCompleted = index < currentStepIndex;
                    const isCurrent = index === currentStepIndex;
                    const Icon = step.icon;

                    return (
                      <div key={step.key} className="flex items-center gap-4 relative">
                        {/* Garis Penghubung Vertikal */}
                        {index < steps.length - 1 && (
                          <div 
                            className="absolute top-6 left-[15px] w-0.5 h-full"
                            style={{ 
                              background: isCompleted ? "#0ea5e9" : "rgba(51, 65, 85, 0.3)" 
                            }}
                          />
                        )}

                        {/* Icon Step */}
                        <div 
                          className={`w-[30px] h-[30px] rounded-full flex items-center justify-center flex-shrink-0 z-10 transition-colors duration-300 ${
                            isCurrent ? "bg-sky-500 text-white shadow-lg shadow-sky-500/30" : 
                            isCompleted ? "bg-sky-500/20 text-sky-400 border border-sky-500/40" : 
                            "bg-slate-800 text-slate-600 border border-slate-700"
                          }`}
                        >
                          <Icon className="text-xs" />
                        </div>

                        {/* Teks Step */}
                        <div>
                          <p className={`text-sm font-semibold ${isCurrent ? "text-white" : isCompleted ? "text-slate-300" : "text-slate-600"}`}>
                            {step.key}
                          </p>
                          {isCurrent && (
                            <p className="text-xs text-sky-400 mt-0.5">Sedang berlangsung</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </SectionCard>
            </div>
          </div>

          {/* Row 2: Informasi Pemasangan & Jadwal Penanganan */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* Informasi Pemasangan */}
            <SectionCard title="Informasi Pemasangan" icon={FaEthernet}>
              <div className="space-y-4">
                {[
                  { icon: FaEthernet, label: "Tipe Kabel", value: psbData.installation_info.cable_type, color: "text-amber-400" },
                  { icon: FaPlug, label: "Panjang Kabel", value: psbData.installation_info.cable_length, color: "text-emerald-400" },
                  { icon: FaRoute, label: "Modem / ONT", value: psbData.installation_info.modem, color: "text-cyan-400" },
                  { icon: FaServer, label: "Splitter Port", value: psbData.installation_info.splitter, color: "text-violet-400" },
                  { icon: FaPlug, label: "Konektor", value: psbData.installation_info.connector, color: "text-slate-400" },
                ].map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div 
                      key={index} 
                      className="flex items-center justify-between p-3 rounded-xl"
                      style={{ background: "rgba(15, 23, 42, 0.6)", border: "1px solid rgba(51, 65, 85, 0.2)" }}
                    >
                      <div className="flex items-center gap-3">
                        <Icon className={`${item.color} text-sm`} />
                        <span className="text-sm text-slate-400">{item.label}</span>
                      </div>
                      <span className="text-sm font-semibold text-gray-100 text-right">{item.value}</span>
                    </div>
                  );
                })}

                {/* Catatan Tambahan */}
                <div className="mt-4 p-3 rounded-xl bg-slate-800/40 border border-slate-700/40">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Catatan Teknis</p>
                  <p className="text-sm text-slate-300 leading-relaxed">{psbData.installation_info.additional_notes}</p>
                </div>
              </div>
            </SectionCard>

            {/* Jadwal Penanganan (Timeline) */}
            <SectionCard title="Jadwal Penanganan" icon={FaCalendarCheck}>
              <div className="relative border-l-2 border-slate-700 ml-2 space-y-6 pb-2">
                {psbData.schedules.map((schedule) => {
                  const isReschedule = schedule.status === "Dijadwalkan Ulang";
                  const isDone = schedule.status === "Selesai Dikerjakan";
                  const isActive = schedule.status === "Akan Datang";

                  return (
                    <div key={schedule.id} className="relative pl-6">
                      {/* Dot Indicator */}
                      <div 
                        className="absolute -left-[9px] top-1 w-4 h-4 rounded-full border-2"
                        style={{
                          background: isReschedule ? "#0B1426" : isDone ? "#0B1426" : isActive ? "#0B1426" : "#0B1426",
                          borderColor: isReschedule ? "#f59e0b" : isDone ? "#34d399" : isActive ? "#0ea5e9" : "#64748b",
                        }}
                      >
                        <div className="w-full h-full rounded-full flex items-center justify-center">
                          {isDone && <FaCircleCheck className="text-[8px] text-emerald-400" />}
                          {isActive && <FaClock className="text-[8px] text-sky-400" />}
                          {isReschedule && <FaHourglassStart className="text-[8px] text-amber-400" />}
                        </div>
                      </div>
                      
                      <div className="text-sm">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-bold text-slate-200">{schedule.date}</span>
                          <span className="text-slate-500 text-xs">{schedule.time} WIB</span>
                        </div>
                        
                        <p className="text-xs text-slate-400 mb-2">Teknisi: <span className="text-slate-300 font-medium">{schedule.technician}</span></p>

                        <StatusBadge status={schedule.status} />

                        {isReschedule && schedule.reason && (
                          <div className="mt-2 p-2 rounded-lg bg-amber-500/5 border border-amber-500/20">
                            <p className="text-xs text-amber-300/80 leading-relaxed">
                              <span className="font-semibold text-amber-400">Alasan:</span> {schedule.reason}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </SectionCard>
          </div>

        </main>
      </DashboardLayout>
    </>
  );
};